<script setup lang="ts">
import type { EmblaCarouselType, EmblaOptionsType, EmblaPluginType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-vue'
import { computed, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    options?: EmblaOptionsType
    plugins?: EmblaPluginType[]
    gap?: number
    updateOnSettle?: boolean
  }>(),
  {
    options: () => ({}),
    plugins: () => [],
    gap: 8,
    updateOnSettle: false,
  },
)

// Selected snap. Updated only by user interaction; on init/reinit the carousel follows the model.
const index = defineModel<number>('index', { default: 0 })

const selectedIndex = ref(index.value)
const canGoToPrev = ref(false)
const canGoToNext = ref(false)

const startSnap = index.value
const [viewport, api] = useEmblaCarousel(
  computed(() => ({ startSnap, ...props.options })),
  computed(() => props.plugins),
)

function syncState(instance: EmblaCarouselType) {
  selectedIndex.value = instance.selectedSnap()
  canGoToPrev.value = instance.canGoToPrev()
  canGoToNext.value = instance.canGoToNext()
}

function restoreIndex(instance: EmblaCarouselType) {
  if (instance.selectedSnap() !== index.value) {
    instance.goTo(index.value, true)
  }
  syncState(instance)
}

watch(
  api,
  (instance, _, onCleanup) => {
    if (!instance) {
      return
    }

    const onSelect = () => {
      syncState(instance)
      if (!props.updateOnSettle) {
        index.value = selectedIndex.value
      }
    }
    const onSettle = () => {
      if (props.updateOnSettle) {
        index.value = instance.selectedSnap()
      }
    }
    const onReInit = () => restoreIndex(instance)

    restoreIndex(instance)
    instance.on('select', onSelect)
    instance.on('settle', onSettle)
    instance.on('reinit', onReInit)
    onCleanup(() => {
      instance.off('select', onSelect)
      instance.off('settle', onSettle)
      instance.off('reinit', onReInit)
    })
  },
  { immediate: true },
)

watch(index, value => {
  if (api.value && value !== api.value.selectedSnap()) {
    api.value.goTo(value)
  }
})

defineExpose({ api, selectedIndex, canGoToPrev, canGoToNext })
</script>

<template lang="pug">
.carousel.overflow-hidden.min-w-0(ref="viewport")
  .carousel-container.flex(:style="{ gap: `${gap}px` }")
    slot(:api="api" :selectedIndex="selectedIndex")
</template>

<style scoped>
.carousel-container {
  touch-action: pan-y pinch-zoom;
}

.carousel-container > :deep(*) {
  --uno: flex-shrink-0 flex-grow-0 min-w-0;
}
</style>
